import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useInstagramProfiles } from '@/hooks/useInstagramProfiles';
import { useAnalytics } from '@/hooks/useAnalytics';
import { POSTS_GOAL_WEEKLY, POSTS_GOAL_MONTHLY, countPostsAdded } from '@/utils/analyticsConstants';
// DashboardSidebar and DashboardHeader are now in DashboardLayout
import { NextScrapingProgress } from '@/components/NextScrapingProgress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { TabButton } from '@/components/ui/tab-button';
import { cn } from '@/lib/utils';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  PieChart,
  Pie,
} from 'recharts';
import {
  Users,
  FileText,
  TrendingUp,
  Target,
  Activity,
  Sparkles,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Award,
  BarChart3,
  LineChart,
  Clock,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { subDays, format, differenceInDays } from 'date-fns';
import { pl } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import foxDetective from '@/assets/fox-detective.webp';
import PostSpeedometer from '@/components/PostSpeedometer';
import { AnalyticsSkeleton } from '@/components/skeletons/AnalyticsSkeleton';

type TimeRange = 'week' | 'month';
type TabValue = 'overview' | 'growth' | 'content' | 'insights';

interface DailyMetric {
  date: string;
  dateKey: string;
  followers: number;
  change: number;
  isPositive: boolean;
  isFuture: boolean;
  hasData: boolean;
  postsAdded: number;
}

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// Stat Card Component
const StatCard = ({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  color = 'blue',
  delay = 0,
}: {
  title: string;
  value: string | number;
  change?: number | null;
  changeLabel?: string;
  icon: React.ElementType;
  color?: 'blue' | 'purple' | 'green' | 'orange';
  delay?: number;
}) => {
  // For "Wzrost" card, check if value starts with "+"
  const isGrowthCard = title === "Wzrost";
  const isGrowthPositive = isGrowthCard && typeof value === 'string' && value.startsWith('+');
  const isGrowthNegative = isGrowthCard && typeof value === 'string' && value.startsWith('-');
  
  const isPositive = change !== undefined && change !== null && change >= 0;

  // Color schemes - more subtle
  const colorSchemes = {
    blue: {
      border: "border-blue-200/50 dark:border-blue-500/30",
      iconBg: "bg-blue-50 dark:bg-blue-500/15",
      iconText: "text-blue-500",
      text: "text-muted-foreground"
    },
    purple: {
      border: "border-purple-200/50 dark:border-purple-500/30",
      iconBg: "bg-purple-50 dark:bg-purple-500/15",
      iconText: "text-purple-500",
      text: "text-muted-foreground"
    },
    green: {
      border: "border-emerald-200/50 dark:border-emerald-500/30",
      iconBg: "bg-emerald-50 dark:bg-emerald-500/15",
      iconText: "text-emerald-500",
      text: "text-muted-foreground"
    },
    orange: {
      border: "border-orange-200/50 dark:border-orange-500/30",
      iconBg: "bg-orange-50 dark:bg-orange-500/15",
      iconText: "text-orange-500",
      text: "text-muted-foreground"
    }
  };

  const scheme = colorSchemes[color];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="h-full"
    >
      <Card className={cn(
        "relative overflow-hidden border shadow-sm h-full flex flex-col glass-card",
        isGrowthPositive ? "border-emerald-200/50" : 
        isGrowthNegative ? "border-red-200/50" :
        scheme.border
      )}>
        <div className={cn("absolute top-0 right-0 w-16 sm:w-24 h-16 sm:h-24 opacity-20 rounded-bl-full", scheme.iconBg)} />
        <CardContent className="p-3 sm:p-5 flex flex-col h-full relative z-10">
          <div className="flex items-start justify-between mb-auto">
            <div className="min-w-0 flex-1">
              <p className={cn("text-xs sm:text-sm font-medium truncate", scheme.text)}>{title}</p>
              <h3 className={cn(
                "text-lg sm:text-2xl font-bold mt-0.5 sm:mt-1 truncate tabular-nums",
                isGrowthPositive ? "text-emerald-700 dark:text-emerald-400" :
                isGrowthNegative ? "text-red-700 dark:text-red-400" :
                "text-foreground"
              )}>{value}</h3>
            </div>
            <div className={cn(
              "p-1.5 sm:p-2.5 rounded-lg sm:rounded-xl flex-shrink-0 ml-2",
              isGrowthPositive ? "bg-emerald-100 dark:bg-emerald-500/15" :
              isGrowthNegative ? "bg-red-100 dark:bg-red-500/15" :
              scheme.iconBg
            )}>
              <Icon className={cn(
                "w-4 h-4 sm:w-5 sm:h-5",
                isGrowthPositive ? "text-emerald-600" : 
                isGrowthNegative ? "text-red-600" :
                scheme.iconText
              )} />
            </div>
          </div>
          
          {change !== undefined && change !== null && (
            <div className="flex items-center gap-1.5 sm:gap-2 mt-2 sm:mt-4 pt-2 sm:pt-3 border-t border-border/30">
              <Badge
                className={cn(
                  "text-[10px] sm:text-xs font-semibold border-0 px-1.5 sm:px-2",
                  isPositive 
                    ? "bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                    : "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400"
                )}
              >
                {isPositive ? '+' : ''}{change}
              </Badge>
              {changeLabel && (
                <span className="text-[10px] sm:text-xs text-muted-foreground hidden sm:inline">{changeLabel}</span>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Growth Chart Component
// Placeholder data for blurred empty state chart
const PLACEHOLDER_GROWTH_DATA = [
  { date: 'Pon', displayChange: 3, change: 3, hasData: true, isPositive: true, postsAdded: 0 },
  { date: 'Wt', displayChange: 8, change: 8, hasData: true, isPositive: true, postsAdded: 0 },
  { date: 'Śr', displayChange: -2, change: -2, hasData: true, isPositive: false, postsAdded: 0 },
  { date: 'Czw', displayChange: 12, change: 12, hasData: true, isPositive: true, postsAdded: 0 },
  { date: 'Pt', displayChange: 5, change: 5, hasData: true, isPositive: true, postsAdded: 0 },
  { date: 'Sob', displayChange: 15, change: 15, hasData: true, isPositive: true, postsAdded: 0 },
  { date: 'Nd', displayChange: 9, change: 9, hasData: true, isPositive: true, postsAdded: 0 },
];

const GrowthChart = ({ data, timeRange }: { data: DailyMetric[]; timeRange: TimeRange }) => {
  const hasData = data.some(d => d.hasData);

  if (!hasData) {
    return (
      <div className="relative">
        {/* Blurred placeholder chart */}
        <div className="blur-[6px] opacity-50 pointer-events-none select-none">
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={PLACEHOLDER_GROWTH_DATA} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorPlaceholder" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} />
                <Area type="monotone" dataKey="displayChange" stroke="#10b981" strokeWidth={2} fill="url(#colorPlaceholder)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Overlay with message */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/40 backdrop-blur-[2px] rounded-xl">
          <div className="w-14 h-14 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <TrendingUp className="h-7 w-7 text-primary" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-1.5">
            Zbieramy dane
          </h3>
          <p className="text-sm text-muted-foreground max-w-md text-center px-4">
            Potrzebujemy jeszcze kilku dni danych. Wróć wkrótce!
          </p>
        </div>
      </div>
    );
  }

  const chartData = data.map(d => ({
    ...d,
    displayChange: d.hasData ? d.change : 0,
  }));

  // Calculate if overall trend is negative for color selection
  const hasNegativeValues = chartData.some(d => d.displayChange < 0);
  const isOverallNegative = chartData.reduce((sum, d) => sum + d.displayChange, 0) < 0;
  const minValue = Math.min(...chartData.map(d => d.displayChange));
  const hasDeepDips = minValue < -15; // Deep dips = red, small dips = orange
  
  // Calculate Y domain and 0 line position for gradient
  const maxValue = Math.max(...chartData.map(d => d.displayChange));
  const yRange = maxValue - minValue;
  const zeroPosition = yRange > 0 ? ((maxValue - 0) / yRange) * 100 : 50;
  const greenStop = Math.max(0, Math.min(100, zeroPosition - 5));
  const colorStop = Math.max(0, Math.min(100, zeroPosition));

  return (
    <div className="h-80 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorGrowth" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorDecline" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="colorMixed" x1="0" y1="0" x2="0" y2="1">
              <stop offset={`0%`} stopColor="#10b981" stopOpacity={0.3}/>
              <stop offset={`${greenStop}%`} stopColor="#10b981" stopOpacity={0.2}/>
              <stop offset={`${colorStop}%`} stopColor="#ef4444" stopOpacity={0.2}/>
              <stop offset={`100%`} stopColor="#ef4444" stopOpacity={0.3}/>
            </linearGradient>
            <linearGradient id="colorGentle" x1="0" y1="0" x2="0" y2="1">
              <stop offset={`0%`} stopColor="#10b981" stopOpacity={0.3}/>
              <stop offset={`${greenStop}%`} stopColor="#10b981" stopOpacity={0.2}/>
              <stop offset={`${colorStop}%`} stopColor="#f97316" stopOpacity={0.2}/>
              <stop offset={`100%`} stopColor="#f97316" stopOpacity={0.3}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6b7280', fontSize: 12 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#6b7280', fontSize: 12 }}
            tickFormatter={(value) => value > 0 ? `+${value}` : value}
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                const isPositiveValue = data.change >= 0;
                return (
                  <div className="bg-white dark:bg-gray-900 p-3 rounded-lg shadow-lg border">
                    <p className="text-sm font-medium">{label}</p>
                    <p className={cn(
                      "text-lg font-bold",
                      isPositiveValue ? "text-emerald-600" : "text-red-600"
                    )}>
                      {isPositiveValue ? '+' : ''}{data.change} obserwujących
                    </p>
                    {data.postsAdded > 0 && (
                      <p className="text-sm text-purple-600 font-medium mt-1">
                        +{data.postsAdded} {data.postsAdded === 1 ? 'nowy post' : 'nowe posty'}
                      </p>
                    )}
                    {!data.hasData && (
                      <p className="text-xs text-muted-foreground mt-1">Brak danych</p>
                    )}
                  </div>
                );
              }
              return null;
            }}
          />
          <Area
            type="monotone"
            dataKey="displayChange"
            stroke={isOverallNegative ? "#ef4444" : "#10b981"}
            strokeWidth={2}
            fill={hasNegativeValues && !isOverallNegative ? (hasDeepDips ? "url(#colorMixed)" : "url(#colorGentle)") : isOverallNegative ? "url(#colorDecline)" : "url(#colorGrowth)"}
            dot={(props: any) => {
              const { cx, cy, payload, index } = props;
              if (index === 0 || !payload?.postsAdded || payload.postsAdded <= 0) return <circle key={`empty-${index}`} r={0} />;
              return (
                <g key={`post-marker-${payload.dateKey}`}>
                  <circle cx={cx} cy={cy} r={6} fill="#8b5cf6" stroke="#fff" strokeWidth={2} />
                  {payload.postsAdded > 1 && (
                    <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle"
                          fill="#fff" fontSize={8} fontWeight="bold">
                      {payload.postsAdded}
                    </text>
                  )}
                </g>
              );
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main Analytics Component
const Analytics = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { profiles, isLoading: isProfilesLoading } = useInstagramProfiles(user?.id || null);
  const { data: analyticsData, isLoading: isAnalyticsLoading, error } = useAnalytics(user?.id);
  const [timeRange, setTimeRange] = useState<TimeRange>('week');
  const [activeTab, setActiveTab] = useState<TabValue>('overview');

  const isLoading = isProfilesLoading || isAnalyticsLoading;

  // Check if scrape has happened today
  const today = new Date();
  const scrapeHappened = today.getHours() > 23 || (today.getHours() === 23 && today.getMinutes() >= 30);

  // Calculate metrics
  const metrics = useMemo(() => {
    if (!analyticsData?.historicalMetrics || analyticsData.historicalMetrics.length < 2) {
      return null;
    }

    const { historicalMetrics } = analyticsData;
    const mostRecent = historicalMetrics[0];

    // Find baseline: try exact period match first, then fall back to oldest available
    const daysBack = timeRange === 'week' ? 7 : 30;
    const targetDate = subDays(new Date(mostRecent.metric_date), daysBack);
    const targetTime = targetDate.getTime();
    const toleranceTime = subDays(targetDate, 2).getTime();

    const baseline = historicalMetrics.find(m => {
      const dataTime = new Date(m.metric_date).getTime();
      return dataTime <= targetTime && dataTime >= toleranceTime;
    }) || historicalMetrics[historicalMetrics.length - 1]; // Fall back to oldest available

    const followersChange = mostRecent.followers_count - baseline.followers_count;
    const followingChange = mostRecent.following_count - baseline.following_count;
    const growthPercent = baseline.followers_count > 0
      ? (followersChange / baseline.followers_count) * 100
      : 0;

    // Posts: count only additions (positive day-over-day deltas), not net change
    const baselineIdx = historicalMetrics.indexOf(baseline);
    const periodMetrics = historicalMetrics.slice(0, baselineIdx + 1).reverse(); // chronological
    const postsChange = countPostsAdded(periodMetrics.map(m => m.posts_count));

    // Calculate posting frequency (posts per week)
    const daysSpan = Math.max(1, differenceInDays(
      new Date(mostRecent.metric_date),
      new Date(baseline.metric_date)
    ));
    const postsPerWeek = (postsChange / daysSpan) * 7;

    return {
      currentFollowers: mostRecent.followers_count,
      currentFollowing: mostRecent.following_count,
      currentPosts: mostRecent.posts_count,
      followersChange,
      followingChange,
      postsChange,
      growthPercent,
      postsPerWeek,
      baselineDate: baseline.metric_date,
    };
  }, [analyticsData?.historicalMetrics, timeRange]);

  // Build chart data
  const chartData = useMemo((): DailyMetric[] => {
    if (!analyticsData?.historicalMetrics || analyticsData.historicalMetrics.length === 0) return [];

    const { historicalMetrics } = analyticsData;
    const todayKey = format(today, 'yyyy-MM-dd');

    // Show last 7 or 30 days
    const daysToShow = timeRange === 'week' ? 7 : 30;
    const slicedMetrics = historicalMetrics.slice(0, daysToShow + 1).reverse();

    const result = slicedMetrics.slice(1).map((metric, index) => {
      const prevMetric = slicedMetrics[index];
      const change = metric.followers_count - prevMetric.followers_count;
      const date = new Date(metric.metric_date);

      return {
        date: timeRange === 'week'
          ? format(date, 'EEE', { locale: pl })
          : format(date, 'd MMM', { locale: pl }),
        dateKey: metric.metric_date,
        followers: metric.followers_count,
        change,
        isPositive: change >= 0,
        isFuture: false,
        hasData: true,
        postsAdded: Math.max(0, metric.posts_change),
      };
    });

    // With only 1 data point the chart renders a dot instead of a line.
    // Prepend a zero-change baseline so Recharts can draw a connecting line.
    if (result.length === 1 && slicedMetrics.length >= 1) {
      const baseMetric = slicedMetrics[0];
      const baseDate = new Date(baseMetric.metric_date);
      result.unshift({
        date: timeRange === 'week'
          ? format(baseDate, 'EEE', { locale: pl })
          : format(baseDate, 'd MMM', { locale: pl }),
        dateKey: baseMetric.metric_date,
        followers: baseMetric.followers_count,
        change: 0,
        isPositive: true,
        isFuture: false,
        hasData: true,
        postsAdded: 0,
      });
    }

    return result;
  }, [analyticsData?.historicalMetrics, timeRange, scrapeHappened]);

  // Loading state
  if (isLoading) {
    return <AnalyticsSkeleton />;
  }

  if (error) {
    return (
      <main className="flex-1 p-3 md:p-6 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={() => navigate('/ustawienia')}>
              Uzupełnij profil
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  return (
    <main className="flex-1 overflow-y-auto p-3 md:p-6">
      <div className="max-w-[1200px] mx-auto space-y-6">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full"
            >
              <NextScrapingProgress />
            </motion.div>

            {/* Time Range Selector */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="flex justify-start"
            >
              <div className="glass-card rounded-xl p-1 border border-border/50 inline-flex flex-wrap gap-1">
                <TabButton
                  isActive={timeRange === 'week'}
                  onClick={() => setTimeRange('week')}
                  variant="gradient"
                >
                  Ostatnie 7 dni
                </TabButton>
                <TabButton
                  isActive={timeRange === 'month'}
                  onClick={() => setTimeRange('month')}
                  variant="gradient"
                >
                  Ostatnie 30 dni
                </TabButton>
              </div>
            </motion.div>

            {/* Stats Grid - 2x2 on mobile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <StatCard
                title="Obserwujący"
                value={metrics?.currentFollowers.toLocaleString('pl-PL') ?? '-'}
                change={metrics?.followersChange}
                icon={Users}
                color="blue"
                delay={0}
              />
              <StatCard
                title="Obserwowani"
                value={metrics?.currentFollowing.toLocaleString('pl-PL') ?? '-'}
                change={metrics?.followingChange}
                icon={Target}
                color="purple"
                delay={0.1}
              />
              <StatCard
                title="Posty"
                value={metrics?.currentPosts.toLocaleString('pl-PL') ?? '-'}
                change={metrics?.postsChange}
                icon={FileText}
                color="green"
                delay={0.2}
              />
              <StatCard
                title="Wzrost"
                value={metrics ? `${metrics.growthPercent >= 0 ? '+' : ''}${metrics.growthPercent.toFixed(2)}%` : '-'}
                changeLabel={timeRange === 'month' ? 'miesięczny' : 'tygodniowy'}
                icon={TrendingUp}
                color="orange"
                delay={0.3}
              />
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="space-y-6">
              <div className="glass-card rounded-xl p-1 border border-border/50 grid grid-cols-2 sm:grid-cols-4 gap-1">
                <TabButton
                  isActive={activeTab === 'overview'}
                  onClick={() => setActiveTab('overview')}
                  variant="gradient"
                  className="flex items-center gap-1.5 sm:gap-2 justify-center"
                >
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Przegląd</span>
                </TabButton>
                <TabButton
                  isActive={activeTab === 'growth'}
                  onClick={() => setActiveTab('growth')}
                  variant="gradient"
                  className="flex items-center gap-1.5 sm:gap-2 justify-center"
                >
                  <LineChart className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Wzrost</span>
                </TabButton>
                <TabButton
                  isActive={activeTab === 'content'}
                  onClick={() => setActiveTab('content')}
                  variant="gradient"
                  className="flex items-center gap-1.5 sm:gap-2 justify-center"
                >
                  <Sparkles className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Content</span>
                </TabButton>
                <TabButton
                  isActive={activeTab === 'insights'}
                  onClick={() => setActiveTab('insights')}
                  variant="gradient"
                  className="flex items-center gap-1.5 sm:gap-2 justify-center"
                >
                  <Zap className="w-4 h-4" />
                  <span className="text-xs sm:text-sm">Insights</span>
                </TabButton>
              </div>

              <AnimatePresence mode="wait">
                <TabsContent value="overview" className="space-y-6">
                  <div className="grid lg:grid-cols-3 gap-6 lg:items-stretch">
                    {/* Main Chart */}
                    <Card className="lg:col-span-2 border-0 shadow-lg flex flex-col">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold flex items-center gap-2">
                          <Activity className="w-5 h-5 text-emerald-500" />
                          Wzrost dzienny
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="flex-1">
                        <GrowthChart data={chartData} timeRange={timeRange} />
                      </CardContent>
                    </Card>

                    {/* Side Cards - stretch to match chart */}
                    <div className="flex flex-col gap-4">
                      {/* Post Speed Gauge */}
                      <Card className="glass-card border border-border/50 flex-1 flex flex-col">
                        <CardHeader className="pb-1">
                          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                            <Zap className="w-4 h-4" />
                            Prędkość postowania
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 pt-0 flex-1 flex items-center">
                          <PostSpeedometer
                            postsCount={metrics?.postsChange ?? 0}
                            timeRange={timeRange}
                          />
                        </CardContent>
                      </Card>

                      {/* Content Summary Card */}
                      <Card className="glass-card border border-border/50">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                            <Sparkles className="w-4 h-4" />
                            Content
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="text-center">
                              <p className="text-xl font-bold">{analyticsData?.inspirationIdeasCount ?? 0}</p>
                              <p className="text-xs text-muted-foreground">Inspiracji</p>
                            </div>
                            <div className="text-muted-foreground">+</div>
                            <div className="text-center">
                              <p className="text-xl font-bold">{analyticsData?.creatorIdeasCount ?? 0}</p>
                              <p className="text-xs text-muted-foreground">Twoich</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="growth" className="space-y-6">
                  <Card className="border-0 shadow-lg">
                    <CardHeader>
                      <CardTitle>Szczegółowy wzrost</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <GrowthChart data={chartData} timeRange={timeRange} />
                      <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-foreground tabular-nums">
                            {metrics?.postsPerWeek?.toFixed(1) ?? '-'}
                          </p>
                          <p className="text-sm text-muted-foreground">Postów/tydzień</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-foreground tabular-nums">
                            {analyticsData?.historicalMetrics?.length ?? '-'}
                          </p>
                          <p className="text-sm text-muted-foreground">Dni z danymi</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                                <TabsContent value="content" className="space-y-6">
                  {/* Content Stats Overview */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="glass-card border border-border/50">
                      <CardContent className="p-5 text-center">
                        <p className="text-4xl font-bold text-foreground tabular-nums">{analyticsData?.inspirationIdeasCount ?? 0}</p>
                        <p className="text-sm text-muted-foreground mt-1">Inspiracji</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="glass-card border border-border/50">
                      <CardContent className="p-5 text-center">
                        <p className="text-4xl font-bold text-foreground tabular-nums">{analyticsData?.creatorIdeasCount ?? 0}</p>
                        <p className="text-sm text-muted-foreground mt-1">Twoich pomysłów</p>
                      </CardContent>
                    </Card>
                    
                    <Card className="glass-card border border-border/50">
                      <CardContent className="p-5 text-center">
                        <p className="text-4xl font-bold text-foreground tabular-nums">{analyticsData?.inspirationAvgScore?.toFixed(1) ?? '-'}</p>
                        <p className="text-sm text-muted-foreground mt-1">Średni viral score</p>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Viral Score Breakdown */}
                  <Card className="glass-card border border-border/50">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-base font-semibold">
                        <Award className="w-4 h-4 text-amber-500" />
                        Viral Score Breakdown
                        <span className="text-xs font-normal text-muted-foreground ml-2">(9 inspiracji)</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        {/* High */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 w-32">
                            <div className="w-2 h-2 rounded-full bg-emerald-500" />
                            <span className="text-sm">Wysoki (70+)</span>
                          </div>
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: '33%' }} />
                          </div>
                          <span className="w-8 text-right text-sm font-medium">3</span>
                        </div>

                        {/* Medium */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 w-32">
                            <div className="w-2 h-2 rounded-full bg-yellow-500" />
                            <span className="text-sm">Średni (40-69)</span>
                          </div>
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-yellow-500 rounded-full" style={{ width: '56%' }} />
                          </div>
                          <span className="w-8 text-right text-sm font-medium">5</span>
                        </div>

                        {/* Low */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2 w-32">
                            <div className="w-2 h-2 rounded-full bg-red-500" />
                            <span className="text-sm">Niski (0-39)</span>
                          </div>
                          <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-red-500 rounded-full" style={{ width: '22%' }} />
                          </div>
                          <span className="w-8 text-right text-sm font-medium">2</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Content Mix */}
                  <Card className="glass-card border border-border/50">
                    <CardHeader className="pb-4">
                      <CardTitle className="flex items-center gap-2 text-base font-semibold">
                        <Sparkles className="w-4 h-4 text-purple-500" />
                        Skład contentu
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-8">
                        <div className="flex-1">
                          <div className="h-3 rounded-full overflow-hidden flex bg-muted">
                            <div
                              className="bg-purple-500 h-full transition-all duration-500"
                              style={{ width: `${(analyticsData?.inspirationIdeasCount ?? 0) / ((analyticsData?.inspirationIdeasCount ?? 0) + (analyticsData?.creatorIdeasCount ?? 0)) * 100}%` }}
                            />
                            <div
                              className="bg-emerald-500 h-full transition-all duration-500"
                              style={{ width: `${(analyticsData?.creatorIdeasCount ?? 0) / ((analyticsData?.inspirationIdeasCount ?? 0) + (analyticsData?.creatorIdeasCount ?? 0)) * 100}%` }}
                            />
                          </div>
                          <div className="flex justify-between mt-3 text-sm">
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-purple-500" />
                              <span className="text-muted-foreground">Inspiracje</span>
                              <span className="font-medium">{Math.round((analyticsData?.inspirationIdeasCount ?? 0) / ((analyticsData?.inspirationIdeasCount ?? 0) + (analyticsData?.creatorIdeasCount ?? 0)) * 100)}%</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full bg-emerald-500" />
                              <span className="text-muted-foreground">Twoje</span>
                              <span className="font-medium">{Math.round((analyticsData?.creatorIdeasCount ?? 0) / ((analyticsData?.inspirationIdeasCount ?? 0) + (analyticsData?.creatorIdeasCount ?? 0)) * 100)}%</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="insights" className="space-y-6">
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <Card className="border-0 shadow-lg">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Aktywność
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold">{analyticsData?.totalContentIdeas ?? 0}</p>
                        <p className="text-sm text-muted-foreground">Pomysłów w bibliotece</p>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-lg">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          Źródła
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold">{analyticsData?.totalInspirationSources ?? 0}</p>
                        <p className="text-sm text-muted-foreground">Obserwowanych kont</p>
                      </CardContent>
                    </Card>
                    <Card className="border-0 shadow-lg">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Dni analizy
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold">{analyticsData?.historicalMetrics?.length ?? 0}</p>
                        <p className="text-sm text-muted-foreground">Dni zebranych danych</p>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>
              </AnimatePresence>
            </Tabs>
          </div>
        </main>
  );
};

export default Analytics;
